import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Pencil, Trash2, ChevronDown, ChevronUp, ToggleLeft, ToggleRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};
const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, { headers: getHeaders(), ...opts });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error');
  return data;
};

const SCOPE_LABELS = { asset: 'Equipo', location: 'Ubicación', global: 'Global' };

const RULE_GROUPS = [
  {
    label: 'Contraseña',
    keys: ['password_min_length', 'password_complexity', 'password_max_age_days', 'password_history_count', 'account_lockout_attempts', 'account_lockout_duration'],
  },
  {
    label: 'Bloqueo de pantalla',
    keys: ['screen_lock_timeout', 'screen_lock_require_pass'],
  },
  {
    label: 'Seguridad general',
    keys: ['disable_usb_storage', 'disable_autorun', 'require_uac', 'disable_rdp', 'require_defender'],
  },
];

// enabled = la regla está activada (incluida en rules)
// value   = el valor actual de la regla
function RuleField({ ruleKey, def, enabled, value, onToggle, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f0f0f0', opacity: enabled ? 1 : 0.45 }}>
      {/* checkbox activar/desactivar la regla */}
      <input
        type="checkbox"
        checked={!!enabled}
        onChange={e => onToggle(ruleKey, e.target.checked, def.default)}
        style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
      />
      <span style={{ fontSize: 13, color: '#333', flex: 1 }}>{def.label}</span>
      {/* control del valor — solo interactuable si está activado */}
      {def.type === 'boolean' ? (
        <button
          disabled={!enabled}
          onClick={() => enabled && onChange(ruleKey, !value)}
          style={{ background: 'none', border: 'none', cursor: enabled ? 'pointer' : 'default', color: value ? 'var(--accent)' : '#bbb', padding: 0 }}
        >
          {value ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
        </button>
      ) : (
        <input
          type="number"
          disabled={!enabled}
          min={def.min} max={def.max}
          value={value}
          onChange={e => onChange(ruleKey, parseInt(e.target.value, 10))}
          style={{ width: 70, border: '1px solid #ddd', borderRadius: 4, padding: '4px 8px', fontSize: 13, textAlign: 'center', background: enabled ? '#fff' : '#f5f5f5' }}
        />
      )}
    </div>
  );
}

function PolicyModal({ policy, ruleDefs, onClose, onSaved }) {
  const isEdit = !!policy;
  const [name, setName] = useState(policy?.name || '');
  const [description, setDescription] = useState(policy?.description || '');
  const [scope, setScope] = useState(policy?.scope || 'asset');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // enabled: set de keys activadas; values: valor actual de cada key
  const existingRules = policy?.rules || {};
  const [enabled, setEnabled] = useState(() => new Set(Object.keys(existingRules)));
  const [values, setValues] = useState(() => {
    const init = {};
    if (ruleDefs) {
      Object.keys(ruleDefs).forEach(k => {
        init[k] = k in existingRules ? existingRules[k] : ruleDefs[k].default;
      });
    }
    return init;
  });

  const handleToggle = (key, on) => {
    setEnabled(prev => {
      const next = new Set(prev);
      on ? next.add(key) : next.delete(key);
      return next;
    });
  };

  const handleChange = (key, val) => setValues(v => ({ ...v, [key]: val }));

  const save = async () => {
    if (!name.trim()) { setError('El nombre es obligatorio.'); return; }
    // Solo incluir en rules las keys activadas
    const rules = {};
    enabled.forEach(k => { rules[k] = values[k]; });
    setSaving(true);
    try {
      if (isEdit) {
        await apiFetch(`/policies/${policy.id}`, { method: 'PATCH', body: JSON.stringify({ name, description, rules, scope }) });
      } else {
        await apiFetch('/policies', { method: 'POST', body: JSON.stringify({ name, description, rules, scope }) });
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Error guardando política.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--surface)', borderRadius: 8, width: 560, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{isEdit ? 'Editar política' : 'Nueva política'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#888' }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          {error && <div style={{ background: 'var(--red-dim)', color: 'var(--red)', padding: '8px 12px', borderRadius: 4, marginBottom: 16, fontSize: 13 }}>{error}</div>}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>NOMBRE</label>
            <input value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4, padding: '7px 10px', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>DESCRIPCIÓN</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4, padding: '7px 10px', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>ALCANCE</label>
            <select value={scope} onChange={e => setScope(e.target.value)} style={{ border: '1px solid #ddd', borderRadius: 4, padding: '7px 10px', fontSize: 14 }}>
              <option value="asset">Equipo individual</option>
              <option value="location">Ubicación</option>
              <option value="global">Global (todos los equipos)</option>
            </select>
          </div>

          <div style={{ fontSize: 11, color: '#888', marginBottom: 16 }}>
            Activá el checkbox de cada regla que querés incluir en esta política. Las reglas sin tildar no se aplican al equipo.
          </div>

          {RULE_GROUPS.map(group => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)', marginBottom: 8, paddingBottom: 4, borderBottom: '2px solid #e6f1fb' }}>{group.label}</div>
              {group.keys.filter(k => ruleDefs[k]).map(k => (
                <RuleField
                  key={k}
                  ruleKey={k}
                  def={ruleDefs[k]}
                  enabled={enabled.has(k)}
                  value={values[k]}
                  onToggle={handleToggle}
                  onChange={handleChange}
                />
              ))}
            </div>
          ))}
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#888' }}>{enabled.size} regla{enabled.size !== 1 ? 's' : ''} activada{enabled.size !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '8px 20px', border: '1px solid #ddd', borderRadius: 4, background: 'var(--surface)', cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
            <button onClick={save} disabled={saving} style={{ padding: '8px 20px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ policy, assets, locations, onClose, onSaved }) {
  const [scope, setScope] = useState('global');
  const [targetId, setTargetId] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/policies/${policy.id}/assign`, { method: 'POST', body: JSON.stringify({ scope, target_id: scope === 'global' ? null : targetId || null }) });
      onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--surface)', borderRadius: 8, width: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700 }}>Asignar política</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#888' }}>×</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>ALCANCE</label>
            <select value={scope} onChange={e => { setScope(e.target.value); setTargetId(''); }} style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4, padding: '7px 10px', fontSize: 14 }}>
              <option value="global">Global — todos los equipos</option>
              <option value="location">Ubicación</option>
              <option value="asset">Equipo específico</option>
            </select>
          </div>
          {scope === 'location' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>UBICACIÓN</label>
              <select value={targetId} onChange={e => setTargetId(e.target.value)} style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4, padding: '7px 10px', fontSize: 14 }}>
                <option value="">Seleccionar...</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          {scope === 'asset' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>EQUIPO</label>
              <select value={targetId} onChange={e => setTargetId(e.target.value)} style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4, padding: '7px 10px', fontSize: 14 }}>
                <option value="">Seleccionar...</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.hostname}</option>)}
              </select>
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '7px 18px', border: '1px solid #ddd', borderRadius: 4, background: 'var(--surface)', cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
          <button onClick={save} disabled={saving} style={{ padding: '7px 18px', background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            {saving ? 'Asignando...' : 'Asignar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [ruleDefs, setRuleDefs] = useState({});
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { type: 'create'|'edit'|'assign', policy? }
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    try {
      const [pols, rules, assetList, locList] = await Promise.all([
        apiFetch('/policies'),
        apiFetch('/policies/rules'),
        apiFetch('/assets'),
        apiFetch('/locations'),
      ]);
      setPolicies(pols);
      setRuleDefs(rules);
      setAssets(assetList);
      setLocations(locList);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const deletePolicy = async (id) => {
    if (!window.confirm('¿Eliminar esta política?')) return;
    try { await apiFetch(`/policies/${id}`, { method: 'DELETE' }); load(); } catch {}
  };

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const rulesSummary = (rules) => {
    const entries = Object.entries(rules || {});
    if (!entries.length) return <span style={{ color: '#aaa', fontSize: 12 }}>Sin reglas configuradas</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {entries.slice(0, 5).map(([k, v]) => (
          <span key={k} style={{ color: 'var(--accent)', fontSize: 11, fontWeight: 600 }}>
            {ruleDefs[k]?.label?.split(' ').slice(0, 3).join(' ') || k}: {typeof v === 'boolean' ? (v ? 'Sí' : 'No') : v}
          </span>
        ))}
        {entries.length > 5 && <span style={{ color: '#888', fontSize: 11 }}>+{entries.length - 5} más</span>}
      </div>
    );
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Cargando políticas...</div>;

  return (
    <div style={{ padding: '28px 32px', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={22} color="var(--accent)" />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Políticas de Seguridad</h1>
        </div>
        <button
          onClick={() => setModal({ type: 'create' })}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--ink)', color: 'var(--surface)', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
        >
          <Plus size={16} /> Nueva política
        </button>
      </div>

      {policies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa' }}>
          <Shield size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15 }}>No hay políticas creadas</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Creá tu primera política para distribuirla a los equipos Windows</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {policies.map(p => (
            <div key={p.id} style={{ background: 'var(--surface)', border: '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</span>
                    <span style={{ color: p.is_active ? 'var(--green)' : '#999', fontSize: 11, fontWeight: 600 }}>
                      {p.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                    <span style={{ background: '#f0f0f0', color: '#555', borderRadius: 4, padding: '1px 8px', fontSize: 11 }}>
                      {SCOPE_LABELS[p.scope] || p.scope}
                    </span>
                    {p.assignment_count > 0 && (
                      <span style={{ background: '#e6f1fb', color: 'var(--accent)', borderRadius: 4, padding: '1px 8px', fontSize: 11 }}>
                        {p.assignment_count} asignación{p.assignment_count !== 1 ? 'es' : ''}
                      </span>
                    )}
                  </div>
                  {p.description && <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{p.description}</div>}
                  {rulesSummary(p.rules)}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setModal({ type: 'assign', policy: p })} title="Asignar" style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 4, background: 'var(--surface)', cursor: 'pointer', color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>
                    Asignar
                  </button>
                  <button onClick={() => setModal({ type: 'edit', policy: p })} title="Editar" style={{ padding: 6, border: '1px solid #ddd', borderRadius: 4, background: 'var(--surface)', cursor: 'pointer', color: '#555' }}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => deletePolicy(p.id)} title="Eliminar" style={{ padding: 6, border: '1px solid #ddd', borderRadius: 4, background: 'var(--surface)', cursor: 'pointer', color: 'var(--red)' }}>
                    <Trash2 size={14} />
                  </button>
                  <button onClick={() => toggleExpand(p.id)} style={{ padding: 6, border: '1px solid #ddd', borderRadius: 4, background: 'var(--surface)', cursor: 'pointer', color: '#555' }}>
                    {expanded[p.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>

              {expanded[p.id] && (
                <div style={{ borderTop: '1px solid #f0f0f0', padding: '14px 20px', background: 'var(--bg-subtle)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 10 }}>REGLAS CONFIGURADAS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
                    {Object.entries(p.rules || {}).map(([k, v]) => (
                      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #eee' }}>
                        <span style={{ color: '#555' }}>{ruleDefs[k]?.label || k}</span>
                        <span style={{ fontWeight: 600, color: typeof v === 'boolean' ? (v ? 'var(--green)' : 'var(--red)') : '#333' }}>
                          {typeof v === 'boolean' ? (v ? 'Sí' : 'No') : v}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal?.type === 'create' && (
        <PolicyModal ruleDefs={ruleDefs} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.type === 'edit' && (
        <PolicyModal policy={modal.policy} ruleDefs={ruleDefs} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {modal?.type === 'assign' && (
        <AssignModal policy={modal.policy} assets={assets} locations={locations} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
